import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      navigate("/");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen pt-[68px] lg:pt-[116px] lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Join Physio Essential</p>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">Create Account</h1>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {error && (
              <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="register-error">
                {error}
              </div>
            )}
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]"
                data-testid="register-name"
                required
              />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]"
                data-testid="register-email"
                required
              />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]"
                data-testid="register-password"
                required
                minLength={6}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-glow flex w-full items-center justify-center gap-2 bg-black py-4 font-semibold text-white transition-colors hover:bg-[#2F80ED] disabled:opacity-60"
              data-testid="register-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-black/60">
            Already have an account?{" "}
            <Link to="/login" className="link-underline font-semibold text-black" data-testid="to-login">
              Log in
            </Link>
          </p>
        </motion.div>
      </div>

      <div className="section-dark relative hidden overflow-hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1540205895360-4ad4cffb3aa8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjBwaHlzaWNhbCUyMHRoZXJhcHl8ZW58MHx8fHwxNzg1NjcwNzkyfDA&ixlib=rb-4.1.0&q=85"
          alt="Recovery"
          className="h-full w-full object-cover opacity-50"
        />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-5xl font-extrabold leading-[0.9] tracking-tighter text-white">
            ENGINEERED<br /><span className="text-[#34D8A0]">RECOVERY.</span>
          </h2>
        </div>
      </div>
    </div>
  );
}
